import { type Moment } from 'moment';
import { Layer, NumberInput } from '@carbon/react';
import { ToggleField, TanstackFieldWrapper } from 'components/form-fields-2';
import {
  ProcessFlow,
  step,
  connector,
} from 'components/process-flow/process-flow';
import { useScenarioForm } from './contexts';
import {
  validateRange,
  formatMonths,
  computeDateLabel,
  ltdStyleConnectorLabels,
  ltdStyleDateLabels,
  LAG_MIN,
  LAG_MAX,
  LTD_OFFSET_MIN,
  LTD_OFFSET_MAX,
} from './utils';
import { TIMING_SECTIONS, timingKey } from './timing-sections';
import type { TimingSection } from './timing-sections';
import type { WellLifecycleParams } from './types';
import type { LtdStyleTimingField } from 'types/dps-timing';
import styles from './well-lifecycle-content.module.scss';

interface WellLifecycleContentProps {
  fieldPrefix: string;
  disabled?: boolean;
  referenceDate: Moment;
}

type NumberInputChangeEvent =
  | React.KeyboardEvent<HTMLInputElement>
  | React.MouseEvent<HTMLButtonElement>
  | React.FocusEvent<HTMLInputElement>;

interface NumberInputChangeData {
  direction: string;
  value: number | string;
}

/**
 * Well lifecycle parameters section: LTD and each Create New Well status (one LTD-style
 * section each, from TIMING_SECTIONS), then Permit and DUC.
 * Each phase has a ProcessFlow visualization and number inputs.
 * Used at both global and area level via fieldPrefix.
 * @param {WellLifecycleContentProps} props - The component props
 * @return {JSX.Element} The well lifecycle content
 */
export function WellLifecycleContent({
  fieldPrefix,
  disabled = false,
  referenceDate,
}: WellLifecycleContentProps): JSX.Element {
  const { form, markAreaConfigured } = useScenarioForm();
  const wlPrefix = `${fieldPrefix}.wellLifecycle`;
  const areaId = form.getFieldValue(
    `${fieldPrefix}.id` as `areas[${number}].id`
  ) as number;
  const markConfigured = () => markAreaConfigured(areaId);

  return (
    <div
      className={`${styles.wellLifecycle} ${disabled ? styles.disabled : ''}`}
    >
      <form.Subscribe
        selector={(state) =>
          state.values.areas[Number(fieldPrefix.replace(/\D/g, ''))]
            ?.wellLifecycle
        }
      >
        {(wl) => {
          if (!wl) return null;

          return (
            <>
              {/* LTD, then each Create New Well status, laid out alike */}
              {TIMING_SECTIONS.map((section) => (
                <TimingSectionBlock
                  key={section.prefix}
                  section={section}
                  wl={wl}
                  wlPrefix={wlPrefix}
                  referenceDate={referenceDate}
                  disabled={disabled}
                  onConfigured={markConfigured}
                  toggle={
                    section.category === 'ltd' &&
                    fieldPrefix.startsWith('areas') ? (
                      <form.Field
                        name={`${fieldPrefix}.includeActiveUpside` as any}
                      >
                        {(field: any) => (
                          <TanstackFieldWrapper<boolean> field={field}>
                            {(props) => (
                              <ToggleField
                                id={`${fieldPrefix}-include-active-upside`}
                                labelText="Include Active Upside"
                                disabled={disabled}
                                {...props}
                                onChange={(val: boolean) => {
                                  markConfigured();
                                  props.onChange(val);
                                }}
                              />
                            )}
                          </TanstackFieldWrapper>
                        )}
                      </form.Field>
                    ) : null
                  }
                />
              ))}

              {/* Permit */}
              <h4 className={styles.sectionTitle}>Permit</h4>
              <ProcessFlow
                items={[
                  step(
                    `Reference Date: ${computeDateLabel(referenceDate, [])}`
                  ),
                  connector(formatMonths(wl.permitReferenceToSpudLagMonths)),
                  step(
                    `Spud: ${computeDateLabel(referenceDate, [
                      wl.permitReferenceToSpudLagMonths,
                    ])}`
                  ),
                  connector(formatMonths(wl.permitSpudToCompletionLagMonths)),
                  step(
                    `Completion: ${computeDateLabel(referenceDate, [
                      wl.permitReferenceToSpudLagMonths,
                      wl.permitSpudToCompletionLagMonths,
                    ])}`
                  ),
                  connector(
                    formatMonths(wl.permitCompletionToFirstProductionLagMonths)
                  ),
                  step(
                    `First Production: ${computeDateLabel(referenceDate, [
                      wl.permitReferenceToSpudLagMonths,
                      wl.permitSpudToCompletionLagMonths,
                      wl.permitCompletionToFirstProductionLagMonths,
                    ])}`
                  ),
                ]}
              />
              <div className={styles.paramsGrid}>
                <LifecycleNumberField
                  form={form}
                  name={`${wlPrefix}.permitReferenceToSpudLagMonths`}
                  label="Reference Date → Spud (months)"
                  disabled={disabled}
                  onConfigured={markConfigured}
                />
                <LifecycleNumberField
                  form={form}
                  name={`${wlPrefix}.permitSpudToCompletionLagMonths`}
                  label="Spud → Completion (months)"
                  disabled={disabled}
                  onConfigured={markConfigured}
                />
                <LifecycleNumberField
                  form={form}
                  name={`${wlPrefix}.permitCompletionToFirstProductionLagMonths`}
                  label="Completion → First Production (months)"
                  disabled={disabled}
                  onConfigured={markConfigured}
                />
              </div>

              {/* DUC */}
              <h4 className={styles.sectionTitle}>DUC</h4>
              <p className={styles.sectionDescription}>
                Modeled independently of pace — does not consume development
                pace capacity.
              </p>
              <ProcessFlow
                items={[
                  step(
                    `Reference Date: ${computeDateLabel(referenceDate, [])}`
                  ),
                  connector(formatMonths(wl.ducReferenceToCompletionLagMonths)),
                  step(
                    `Completion: ${computeDateLabel(referenceDate, [
                      wl.ducReferenceToCompletionLagMonths,
                    ])}`
                  ),
                  connector(
                    formatMonths(wl.ducCompletionToFirstProductionLagMonths)
                  ),
                  step(
                    `First Production: ${computeDateLabel(referenceDate, [
                      wl.ducReferenceToCompletionLagMonths,
                      wl.ducCompletionToFirstProductionLagMonths,
                    ])}`
                  ),
                ]}
              />
              <div className={styles.paramsGrid}>
                <LifecycleNumberField
                  form={form}
                  name={`${wlPrefix}.ducReferenceToCompletionLagMonths`}
                  label="Reference Date → Completion (months)"
                  disabled={disabled}
                  onConfigured={markConfigured}
                />
                <LifecycleNumberField
                  form={form}
                  name={`${wlPrefix}.ducCompletionToFirstProductionLagMonths`}
                  label="Completion → First Production (months)"
                  disabled={disabled}
                  onConfigured={markConfigured}
                />
              </div>
            </>
          );
        }}
      </form.Subscribe>
    </div>
  );
}

// ─────────────────────────────────────
// One LTD-style timing section
// ─────────────────────────────────────

interface TimingSectionBlockProps {
  section: TimingSection;
  wl: WellLifecycleParams;
  wlPrefix: string;
  referenceDate: Moment;
  disabled: boolean;
  onConfigured: () => void;
  /** LTD's Include Active Upside toggle; no other section has one. */
  toggle?: React.ReactNode;
}

/**
 * One LTD-style section: Reference Date → start offset → Permit → Spud → Completion → First
 * Production, and its four inputs. LTD and each Create New Well status render this alike.
 * @param {TimingSectionBlockProps} props - The component props
 * @return {JSX.Element} The section
 */
function TimingSectionBlock({
  section,
  wl,
  wlPrefix,
  referenceDate,
  disabled,
  onConfigured,
  toggle = null,
}: TimingSectionBlockProps): JSX.Element {
  const { form } = useScenarioForm();
  const offset = wl[timingKey(section.prefix, 'StartOffsetMonths')];
  const permitToSpud = wl[timingKey(section.prefix, 'PermitToSpudLagMonths')];
  const spudToCompletion =
    wl[timingKey(section.prefix, 'SpudToCompletionLagMonths')];
  const completionToFirstProduction =
    wl[timingKey(section.prefix, 'CompletionToFirstProductionLagMonths')];
  // As the scheduler dates the slot: the offset lands on Spud, Permit is the lag before it.
  const dates = ltdStyleDateLabels(referenceDate, {
    offset,
    permitToSpud,
    spudToCompletion,
    completionToFirstProduction,
  });
  // Each connector is the gap between the two dates beside it (Reference → Permit is the offset
  // less the permit-to-spud lag).
  const gaps = ltdStyleConnectorLabels({
    offset,
    permitToSpud,
    spudToCompletion,
    completionToFirstProduction,
  });
  /**
   * @param {LtdStyleTimingField} field - One of the section's fields.
   * @return {string} The form field name.
   */
  const name = (field: LtdStyleTimingField) =>
    `${wlPrefix}.${timingKey(section.prefix, field)}`;

  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h4 className={styles.sectionTitle}>{section.title}</h4>
          <p className={styles.sectionDescription}>{section.description}</p>
        </div>
        {toggle}
      </div>
      <ProcessFlow
        items={[
          step(`Reference Date: ${computeDateLabel(referenceDate, [])}`),
          connector(gaps.referenceToPermit),
          step(`Permit: ${dates.permit}`),
          connector(gaps.permitToSpud),
          step(`Spud: ${dates.spud}`),
          connector(gaps.spudToCompletion),
          step(`Completion: ${dates.completion}`),
          connector(gaps.completionToFirstProduction),
          step(`First Production: ${dates.firstProduction}`),
        ]}
      />
      <div className={styles.paramsGrid}>
        <LifecycleNumberField
          form={form}
          name={name('StartOffsetMonths')}
          label={`${section.title} Start Offset (months)`}
          allowNegative
          disabled={disabled}
          onConfigured={onConfigured}
        />
        <LifecycleNumberField
          form={form}
          name={name('PermitToSpudLagMonths')}
          label="Permit → Spud (months)"
          disabled={disabled}
          onConfigured={onConfigured}
        />
        <LifecycleNumberField
          form={form}
          name={name('SpudToCompletionLagMonths')}
          label="Spud → Completion (months)"
          disabled={disabled}
          onConfigured={onConfigured}
        />
        <LifecycleNumberField
          form={form}
          name={name('CompletionToFirstProductionLagMonths')}
          label="Completion → First Production (months)"
          disabled={disabled}
          onConfigured={onConfigured}
        />
      </div>
    </>
  );
}

// ─────────────────────────────────────
// Lifecycle number input helper
// ─────────────────────────────────────

interface LifecycleNumberFieldProps {
  form: any;
  name: string;
  label: string;
  allowNegative?: boolean;
  disabled?: boolean;
  onConfigured: () => void;
}

/**
 * A NumberInput wired to a form.Field for lifecycle month parameters. Lags run
 * [0, 120]; the LTD start offset (allowNegative) runs [-120, 120] — the client
 * mirror of the server range validations, surfaced inline.
 * @param {LifecycleNumberFieldProps} props - The component props
 * @return {JSX.Element} The number input field
 */
function LifecycleNumberField({
  form,
  name,
  label,
  allowNegative = false,
  disabled = false,
  onConfigured,
}: LifecycleNumberFieldProps): JSX.Element {
  const min = allowNegative ? LTD_OFFSET_MIN : LAG_MIN;
  const max = allowNegative ? LTD_OFFSET_MAX : LAG_MAX;

  return (
    <form.Field
      key={name}
      name={name}
      validators={{
        onChange: ({ value }: { value: number | string }) =>
          validateRange(min, max)(value),
      }}
    >
      {(field: any) => {
        const errors = field.state.meta.errors || [];
        const errorText = typeof errors[0] === 'string' ? errors[0] : undefined;
        return (
          <Layer>
            <NumberInput
              id={name.replace(/\./g, '-')}
              label={label}
              value={field.state.value}
              min={min}
              max={max}
              step={1}
              disabled={disabled}
              invalid={!!errorText}
              invalidText={errorText || ''}
              onChange={(
                _e: NumberInputChangeEvent,
                data: NumberInputChangeData
              ) => {
                onConfigured();
                if (data.value === '' || data.value === undefined) {
                  field.handleChange('');
                } else {
                  field.handleChange(Number(data.value));
                }
              }}
            />
          </Layer>
        );
      }}
    </form.Field>
  );
}
